import { z } from "zod";

export const generationStageSchema = z.enum(["upload", "submit", "poll", "download", "persist"]);
export const diagnosticCaptureSchema = z.enum(["parsed", "missing", "invalid_json", "oversized", "network_error"]);
export const exactTokenCountSchema = z.string().max(19).regex(/^(0|[1-9]\d*)$/u)
  .refine(value => value.length < 19 || value <= "9223372036854775807", "Token count exceeds bigint range");
export const providerReferenceValueSchema = z.string().min(1).max(320).regex(/^[a-z0-9][a-z0-9_.:-]*$/iu);
export const providerRequestReferenceSchema = z.strictObject({
  value: providerReferenceValueSchema,
  kind: z.enum(["error", "response"]),
  source: z.string().regex(/^(?:(?:error\.|data\.){0,4}(?:request_id|requestId|requestID|RequestId|(?:message|msg):text)|header:(?:x-request-id|x-tt-logid|x-tt-request-id))$/u)
}).refine(ref => ref.source.startsWith("header:") === (ref.kind === "response"), "Reference kind must match source");
export const providerDiagnosticSchema = z.strictObject({
  stage: generationStageSchema,
  capture: diagnosticCaptureSchema,
  httpStatus: z.number().int().min(100).max(599).optional(),
  providerState: providerReferenceValueSchema.optional(),
  code: providerReferenceValueSchema.optional(),
  message: z.string().max(2000),
  references: z.array(providerRequestReferenceSchema).max(16),
  parserVersion: z.literal("2026-10-10"),
  truncated: z.boolean()
});
export const providerUsageSchema = z.strictObject({
  inputTokens: exactTokenCountSchema.optional(),
  outputTokens: exactTokenCountSchema.optional(),
  totalTokens: exactTokenCountSchema.optional(),
  invalidFields: z.array(z.enum(["input", "output", "total"])).max(3)
    .refine(fields => new Set(fields).size === fields.length, "Duplicate invalid fields")
});

export type GenerationStage = z.infer<typeof generationStageSchema>;
export type DiagnosticCapture = z.infer<typeof diagnosticCaptureSchema>;
export type ProviderRequestReference = z.infer<typeof providerRequestReferenceSchema>;
export type ProviderDiagnostic = z.infer<typeof providerDiagnosticSchema>;
export type ProviderUsage = z.infer<typeof providerUsageSchema>;
